import React, { useMemo } from "react"
import { graphql } from "@apollo/client/react/hoc"
import { useQuery } from "@apollo/client"
import { compose } from "ramda"
import { getMember } from "../../queries/member"
import { CURRENT_PERSON_PGA_ID } from "../../queries/profilePhoto"
import { COACH_TOOLS_API } from "../../../apolloClient"
import { getDisplayPhoto } from "../../../utils/memberPhoto"
import ProfileWidget, { LoadingProfile } from "./Profile"
import CompactProfileWidget, { LoadingCompactProfile } from "./CompactProfile"
import ErrorLoadingProfile from "./ErrorLoadingProfile"
import WrapWithFetch from "../WrapWithFetch"

const coachToolsContext = { context: { api: COACH_TOOLS_API } }

const mergeCoachToolsPhoto = (user, coachToolsProfilePhoto) => {
  if (!user) return user
  return {
    ...user,
    profilePhoto: coachToolsProfilePhoto || user.profilePhoto,
    photo: getDisplayPhoto({
      profilePhoto: coachToolsProfilePhoto,
      photo: user.photo,
    }),
  }
}

const useMergedProfileUser = user => {
  const { data } = useQuery(CURRENT_PERSON_PGA_ID, {
    ...coachToolsContext,
    fetchPolicy: "cache-first",
  })
  const coachToolsProfilePhoto =
    data && data.currentPerson && data.currentPerson.profilePhoto
  return useMemo(
    () => mergeCoachToolsPhoto(user, coachToolsProfilePhoto),
    [user, coachToolsProfilePhoto],
  )
}

const ProfileBriefWithCoachToolsPhoto = props => {
  const user = useMergedProfileUser(props.user)
  return <ProfileWidget {...props} user={user} />
}

const attachQuery = compose(
  graphql(getMember, {
    props: ({ data }) => ({
      data,
      loading: data.loading,
      user: data.me,
      error: data.error,
      refetch: data.refetch,
    }),
  }),
)

export const CompactProfileBrief = attachQuery(
  ({ user, loading }) =>
    (loading && <LoadingCompactProfile />) || (
      <CompactProfileWidget user={user} />
    ),
)

const ProfileBriefContent = WrapWithFetch(
  ({ user, loading, isFetching, error, refetch, reload, vertical }) => {
    if (loading || isFetching) {
      return <LoadingProfile vertical={vertical} />
    } else if (error || !user) {
      return <ErrorLoadingProfile {...{ refetch, reload }} />
    }

    return (
      <ProfileBriefWithCoachToolsPhoto
        user={user}
        error={error}
        refetch={refetch}
        vertical={vertical}
      />
    )
  },
)

export default attachQuery(ProfileBriefContent)
